'use client';

import { Mountain, Phone, Mail, MapPin, Shield, Calendar } from 'lucide-react';
import { useState } from 'react';
import AdminLogin from '@/components/AdminLogin';
import AdminDashboard from '@/components/AdminDashboard';

const Footer = (): JSX.Element => {
  const currentYear = new Date().getFullYear();
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminData, setAdminData] = useState<any>(null);

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const footerLinks = [
    { label: 'Home', href: '#home' },
    { label: 'About', href: '#about' },
    { label: 'Gallery', href: '#gallery' },
    { id: 'overview', label: 'Overview', icon: Calendar },
    { label: 'Book Now', href: '#booking' },
    { label: 'Contact', href: '#contact' },
  ];

  const handleAdminLogin = (loginData: any) => {
    setAdminData(loginData);
    setShowAdminLogin(false);
  };

  const handleAdminLogout = () => {
    setAdminData(null);
  };

  if (adminData) {
    return <AdminDashboard onLogout={handleAdminLogout} adminData={adminData} />;
  }

  return (
    <>
      <footer className="bg-[#333333] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Main Footer Content */}
          <div className="py-16">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Brand Section */}
              <div className="lg:col-span-2">
                <div className="flex items-center space-x-2 mb-6">
                  <Mountain className="h-8 w-8 text-[#7C8F75]" />
                  <span className="text-2xl font-bold">Mountain View</span>
                </div>
                <p className="text-white/80 leading-relaxed mb-6 max-w-md">
                  Experience luxury and tranquility in Monroe, NY. Our premier vacation destination 
                  offers unparalleled comfort with modern amenities, breathtaking grounds, and 
                  convenient access to local attractions.
                </p>
                <div className="flex flex-col space-y-3">
                  <a
                    href="tel:718-517-9235"
                    className="flex items-center space-x-3 text-white/80 hover:text-[#7C8F75] transition-colors duration-300"
                  >
                    <Phone className="h-5 w-5" />
                    <span>718-517-9235</span>
                  </a>
                  <a
                    href="mailto:dira4ubp@gmail.com"
                    className="flex items-center space-x-3 text-white/80 hover:text-[#7C8F75] transition-colors duration-300"
                  >
                    <Mail className="h-5 w-5" />
                    <span>dira4ubp@gmail.com</span>
                  </a>
                  <div className="flex items-center space-x-3 text-white/80">
                    <MapPin className="h-5 w-5" />
                    <span>Monroe, NY</span>
                  </div>
                </div>
              </div>

              {/* Quick Links */}
              <div>
                <h3 className="text-xl font-bold mb-6">Quick Links</h3>
                <ul className="space-y-3">
                  {footerLinks.map((link) => (
                    <li key={link.label}>
                      <button
                        onClick={() => scrollToSection(link.href)}
                        className="text-white/80 hover:text-[#7C8F75] transition-colors duration-300 text-left"
                      >
                        {link.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Property Features */}
              <div>
                <h3 className="text-xl font-bold mb-6">Property Features</h3>
                <ul className="space-y-3 text-white/80">
                  <li className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-[#7C8F75] rounded-full"></div>
                    <span>5 Bedrooms, 15 Beds</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-[#7C8F75] rounded-full"></div>
                    <span>3 Full Bathrooms</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-[#7C8F75] rounded-full"></div>
                    <span>On-site Shul & Mikveh</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-[#7C8F75] rounded-full"></div>
                    <span>Beautiful Porches</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-[#7C8F75] rounded-full"></div>
                    <span>Prime Location</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Location Highlights */}
          <div className="border-t border-white/10 py-8">
            <div className="text-center mb-8">
              <h3 className="text-xl font-bold mb-4">Convenient Location</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-white/80">
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-2 h-2 bg-[#7C8F75] rounded-full"></div>
                  <span>4 mins to KJ Munro</span>
                </div>
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-2 h-2 bg-[#7C8F75] rounded-full"></div>
                  <span>7 mins to Woodbury Commons</span>
                </div>
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-2 h-2 bg-[#7C8F75] rounded-full"></div>
                  <span>5 mins to רוח החיים Park</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Footer */}
          <div className="border-t border-white/10 py-8">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="text-white/60 text-sm mb-4 md:mb-0">
                © {currentYear} Mountain View. All rights reserved.
              </div>
              <div className="flex items-center space-x-6">
                {/* Admin Login Button */}
                <button
                  onClick={() => setShowAdminLogin(true)}
                  className="flex items-center space-x-2 text-white/40 hover:text-[#7C8F75] transition-colors duration-300 text-sm group"
                >
                  <Shield className="h-4 w-4 group-hover:scale-110 transition-transform duration-300" />
                  <span>Admin</span>
                </button>
                <div className="text-white/60 text-sm">
                  <span>Made by </span>
                  <a 
                    href="https://stardev.dev" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#7C8F75] hover:text-[#A8B89A] transition-colors duration-300 font-medium"
                  >
                    stardev.dev
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Admin Login Modal */}
      <AdminLogin
        isOpen={showAdminLogin}
        onClose={() => setShowAdminLogin(false)}
        onLogin={handleAdminLogin}
      />
    </>
  );
};

export default Footer;